import { Box, HStack, Icon, Skeleton, Text, VStack } from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { FC } from 'react';
import { MdImage, MdInsertDriveFile, MdPictureAsPdf } from 'react-icons/md';

import { LabDocumentEmptyState } from './LabDocumentEmptyState';
import { LabDocument } from './types';

interface LabDocumentTableProps {
  documents: LabDocument[];
  onDocumentClick: (documentId: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

const getFileIcon = (mimeType: string) => {
  if (mimeType === 'application/pdf') {
    return MdPictureAsPdf;
  }
  if (mimeType.startsWith('image/')) {
    return MdImage;
  }
  return MdInsertDriveFile;
};

const getFileIconColor = (mimeType: string): string => {
  if (mimeType === 'application/pdf') {
    return 'red.500';
  }
  if (mimeType.startsWith('image/')) {
    return 'blue.500';
  }
  return 'text.subtle';
};

const formatUploadDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
};

/**
 * Scrollable list of a patient's lab documents (PDFs and images).
 *
 * Rendered inside the "Labs" tab of the CallV2 SidePane as the default
 * (list) view. When a provider clicks a row the parent connected component
 * (LabDocumentsTab) sets the selected document ID, which swaps this view
 * out for LabDocumentPreview.
 *
 * Each row shows a mime-type icon, the truncated filename, and a formatted
 * upload date. The component handles three states:
 *  - Loading: skeleton placeholder rows.
 *  - Empty: delegates to LabDocumentEmptyState.
 *  - Populated: clickable, keyboard-accessible document rows.
 *
 * Documents arrive pre-sorted (most recent first) from labDocumentsAtom;
 * this component does not sort.
 *
 * Purely presentational — no atoms, no queries, no application state.
 */
export const LabDocumentTable: FC<LabDocumentTableProps> = ({
  documents,
  onDocumentClick,
  isLoading = false,
  emptyMessage,
}) => {
  if (isLoading) {
    return (
      <VStack spacing={0} align="stretch" width="100%">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} height="52px" borderRadius="md" mx={2} mb={2} />
        ))}
      </VStack>
    );
  }

  if (documents.length === 0) {
    return <LabDocumentEmptyState message={emptyMessage} />;
  }

  return (
    <VStack spacing={0} align="stretch" width="100%">
      {documents.map((doc) => (
        <Box
          key={doc.id}
          px={3}
          py={3}
          cursor="pointer"
          borderRadius="md"
          _hover={{ bg: 'bg.subtle' }}
          onClick={() => onDocumentClick(doc.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onDocumentClick(doc.id);
            }
          }}
        >
          <HStack spacing={3} align="center">
            <Icon
              as={getFileIcon(doc.mimeType)}
              boxSize={5}
              color={getFileIconColor(doc.mimeType)}
              flexShrink={0}
            />
            <Box flex={1} minW={0}>
              <Text
                fontSize="sm"
                color="text.default"
                fontWeight={500}
                isTruncated
                title={doc.filename}
              >
                {doc.filename}
              </Text>
              <Text fontSize="xs" color="text.subtle">
                {formatUploadDate(doc.createdAt)}
              </Text>
            </Box>
          </HStack>
        </Box>
      ))}
    </VStack>
  );
};

LabDocumentTable.displayName = 'LabDocumentTable';
